import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";

import { formatDate, getAvatarUrl, getRealFileName, getUploadUrl } from "src/Common/common";
import { can, downloadImage, downloadPdf, handleError } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import WOFormService from "src/Services/API/woform.service";
import ConfirmDialog from "src/Common/confirmdialog";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";
import FormViewerModal from "./FormViewerModal";
import { ChevronIcon, DownloadMenuIcon, DeleteMenuIcon } from "../common/DocumentRowIcons";

// Jobsite Forms uploaded from the technician app (UploadFormScreen) are
// WoAttachment rows with type === 2 (single-table inheritance shares the table
// with technical documents/jobsite photos). The fillable "Digital Forms"
// currently assigned to the WO come from the separate wo_form table
// (order.forms) instead.
const JOBSITE_FORM_TYPE = 2;
// The /attach upload flow uses type 1 for "attachments to the work order"; the
// per-file metadata.attachmentType then narrows it to a jobsite form.
const ATTACHMENT_UPLOAD_FLOW = 1;

// Discriminates the two row kinds that share this table.
const ROW_KIND = { DIGITAL: "digital", FILE: "file" };

const DIGITAL_BADGE_STYLE = "bg-[#EAF3FF] text-[#0058E5]";
const PDF_BADGE_STYLE = "bg-[#FEE2E2] text-[#DC2626]";
const PHOTO_BADGE_STYLE = "bg-[#DCFCE7] text-[#15803D]";
const DEFAULT_BADGE_STYLE = "bg-[#EEF0F9] text-[#5F6477]";

// Square rounded icon tiles shown in front of the file name. Each row kind gets
// its own tinted tile + glyph, matching the Forms table design:
//  - digital forms: blue file-edit glyph on a light-blue tile
//  - PDFs: red PDF glyph on a light-red tile
//  - photos / other files reuse the neutral PDF-style document glyph in the
//    matching tint.
const FILE_TILE = {
  digital: "bg-[#F5F9FF] border-[#DAEBFF]",
  pdf: "bg-[#FEF2F2] border-[#FEE2E2]",
  photo: "bg-[#F0FDF4] border-[#DCFCE7]",
  default: "bg-[#F8F9FE] border-[#E5E8F3]",
};

// Blue file-with-pencil glyph for digital (editable) forms.
const DigitalGlyph = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="21" height="18" viewBox="0 0 21 18" fill="none">
    <g clip-path="url(#clip0_5878_126561)">
      <path
        d="M2.25 0C1.00898 0 0 1.00898 0 2.25V15.75C0 16.991 1.00898 18 2.25 18H11.25C12.491 18 13.5 16.991 13.5 15.75V15.0715C13.4051 15.1102 13.3102 15.1418 13.2117 15.1664L11.0988 15.6937C10.9934 15.7184 10.8879 15.7359 10.7824 15.743C10.7508 15.7465 10.7191 15.75 10.6875 15.75H8.4375C8.22305 15.75 8.02969 15.6305 7.93477 15.4406L7.62539 14.8184C7.56562 14.6988 7.44609 14.625 7.31602 14.625C7.18594 14.625 7.06289 14.6988 7.00664 14.8184L6.69727 15.4406C6.59531 15.648 6.37383 15.7711 6.14531 15.75C5.9168 15.7289 5.71992 15.5707 5.65664 15.3527L5.0625 13.3945L4.71797 14.5477C4.50352 15.2613 3.84609 15.75 3.10078 15.75H2.8125C2.50312 15.75 2.25 15.4969 2.25 15.1875C2.25 14.8781 2.50312 14.625 2.8125 14.625H3.10078C3.35039 14.625 3.56836 14.4633 3.63867 14.2242L4.1625 12.484C4.28203 12.0867 4.64766 11.8125 5.0625 11.8125C5.47734 11.8125 5.84297 12.0867 5.9625 12.484L6.37031 13.841C6.63047 13.623 6.96094 13.5 7.3125 13.5C7.87148 13.5 8.38125 13.8164 8.63086 14.3156L8.78555 14.625H9.09844C8.98945 14.3156 8.96836 13.9781 9.04922 13.6477L9.57656 11.5348C9.675 11.1375 9.87891 10.7789 10.1672 10.4906L13.5 7.15781V5.625H9C8.37773 5.625 7.875 5.12227 7.875 4.5V0H2.25ZM9 0V4.5H13.5L9 0ZM19.3289 4.91133C18.7805 4.36289 17.891 4.36289 17.3391 4.91133L16.3055 5.94492L18.8016 8.44102L19.8352 7.40742C20.3836 6.85898 20.3836 5.96953 19.8352 5.41758L19.3289 4.91133ZM10.9652 11.2852C10.8211 11.4293 10.7191 11.6086 10.6699 11.809L10.1426 13.9219C10.0934 14.1152 10.1496 14.3156 10.2902 14.4563C10.4309 14.5969 10.6312 14.6531 10.8246 14.6039L12.9375 14.0766C13.1344 14.0273 13.3172 13.9254 13.4613 13.7812L18.0035 9.23555L15.5074 6.73945L10.9652 11.2852Z"
        fill="#0065FD"
      />
    </g>
    <defs>
      <clipPath id="clip0_5878_126561">
        <path d="M0 0H20.25V18H0V0Z" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Red PDF/document glyph for uploaded PDF forms.
const PdfGlyph = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
    <g clip-path="url(#clip0_5878_126570)">
      <path
        d="M0 2.25C0 1.00898 1.00898 0 2.25 0H7.875V4.5C7.875 5.12227 8.37773 5.625 9 5.625H13.5V10.6875H6.1875C4.94648 10.6875 3.9375 11.6965 3.9375 12.9375V18H2.25C1.00898 18 0 16.991 0 15.75V2.25ZM13.5 4.5H9V0L13.5 4.5ZM6.1875 12.375H7.3125C8.39883 12.375 9.28125 13.2574 9.28125 14.3438C9.28125 15.4301 8.39883 16.3125 7.3125 16.3125H6.75V17.4375C6.75 17.7469 6.49687 18 6.1875 18C5.87813 18 5.625 17.7469 5.625 17.4375V15.75V12.9375C5.625 12.6281 5.87813 12.375 6.1875 12.375ZM7.3125 15.1875C7.78008 15.1875 8.15625 14.8113 8.15625 14.3438C8.15625 13.8762 7.78008 13.5 7.3125 13.5H6.75V15.1875H7.3125ZM10.6875 12.375H11.8125C12.7441 12.375 13.5 13.1309 13.5 14.0625V16.3125C13.5 17.2441 12.7441 18 11.8125 18H10.6875C10.3781 18 10.125 17.7469 10.125 17.4375V12.9375C10.125 12.6281 10.3781 12.375 10.6875 12.375ZM11.8125 16.875C12.1219 16.875 12.375 16.6219 12.375 16.3125V14.0625C12.375 13.7531 12.1219 13.5 11.8125 13.5H11.25V16.875H11.8125ZM14.625 12.9375C14.625 12.6281 14.8781 12.375 15.1875 12.375H16.875C17.1844 12.375 17.4375 12.6281 17.4375 12.9375C17.4375 13.2469 17.1844 13.5 16.875 13.5H15.75V14.625H16.875C17.1844 14.625 17.4375 14.8781 17.4375 15.1875C17.4375 15.4969 17.1844 15.75 16.875 15.75H15.75V17.4375C15.75 17.7469 15.4969 18 15.1875 18C14.8781 18 14.625 17.7469 14.625 17.4375V15.1875V12.9375Z"
        fill="#EF4444"
      />
    </g>
    <defs>
      <clipPath id="clip0_5878_126570">
        <path d="M0 0H18V18H0V0Z" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const FileTypeIcon = ({ tile, glyph, thumbnailUrl }) =>
  thumbnailUrl ? (
    <img
      src={thumbnailUrl}
      alt=""
      className="w-[39px] h-[39px] shrink-0 rounded-[12px] border border-[#DCFCE7] object-cover"
    />
  ) : (
    <span className={`w-[39px] h-[39px] shrink-0 rounded-[12px] border flex items-center justify-center ${tile}`}>
      {glyph}
    </span>
  );

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const isImageFile = (fileName) => !!fileName && /\.(png|jpe?g|gif|bmp|webp|svg|heic|heif|tiff?)$/i.test(fileName);

// Build the name used when downloading. Prefer the user-facing documentName,
// falling back to the real stored file name. The original file's extension is
// appended when documentName doesn't already carry one.
const getDownloadFileName = (file) => {
  const realFileName = getRealFileName(file.fileName) || file.fileName;
  const baseName = file.documentName?.trim();
  if (!baseName) return realFileName;
  const lastDot = realFileName.lastIndexOf(".");
  const extension = lastDot >= 0 ? realFileName.substring(lastDot) : "";
  return extension && !baseName.toLowerCase().endsWith(extension.toLowerCase()) ? `${baseName}${extension}` : baseName;
};

const ServiceTicketToggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-[20px] w-[36px] shrink-0 items-center rounded-[12px] p-[2px] transition-colors ${
      checked ? "bg-[#0065FD]" : "bg-[#EEF0F9]"
    } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
  >
    <span
      className={`inline-block h-[16px] w-[16px] transform rounded-full bg-white shadow-[0px_1px_2px_rgba(10,12.67,18,0.06),0px_1px_3px_rgba(10,12.67,18,0.10)] transition-transform ${
        checked ? "translate-x-[16px]" : "translate-x-0"
      }`}
    />
  </button>
);

/**
 * Work-order Forms card. Lists two kinds of rows together:
 *  - Digital Forms: the fillable forms currently assigned to the WO
 *    (order.forms / wo_form table). "View" opens the FormViewerModal.
 *  - Uploaded files: PDFs/photos submitted from the technician app's
 *    UploadFormScreen, stored as WoAttachment rows with type === 2.
 *    "View" opens the image/PDF slider.
 */
function WoForms({ order, onRefresh, onAddForm }) {
  const [expanded, setExpanded] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  // Fixed-position coordinates for the open menu. The card has overflow-hidden,
  // so the menu is portaled to the body and positioned from the button's rect,
  // flipping above the button when there isn't room below.
  const [menuPosition, setMenuPosition] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(null);
  const [formViewerShown, setFormViewerShown] = useState(false);

  const digitalForms = useMemo(() => order?.forms || [], [order?.forms]);

  const uploadedFiles = useMemo(() => {
    const attachments = order?.attachments || [];
    return attachments.filter((attachment) => Number(attachment?.type) === JOBSITE_FORM_TYPE);
  }, [order?.attachments]);

  const getDigitalUploaderName = (form) => form?.createdBy?.name || "-";

  const getFileUploaderName = (file) =>
    file?.uploadedBy?.name ||
    file?.uploadedByCustomerUser?.customer?.companyName ||
    file?.uploadedByCustomerUser?.account?.name ||
    "-";

  // Unify both kinds into a single sorted list of rows for the table.
  const rows = useMemo(() => {
    const digitalRows = digitalForms.map((form) => ({
      kind: ROW_KIND.DIGITAL,
      key: `form-${form.id}`,
      id: form.id,
      displayName: form.name || "Untitled Form",
      typeLabel: "Digital Form",
      badgeStyle: DIGITAL_BADGE_STYLE,
      tileStyle: FILE_TILE.digital,
      glyph: <DigitalGlyph />,
      uploaderName: getDigitalUploaderName(form),
      uploaderAvatar: form?.createdBy?.avatar,
      createdAt: form.createdAt,
      // Digital forms don't participate in the service ticket toggle.
      serviceTicketSupported: false,
      raw: form,
    }));

    const fileRows = uploadedFiles.map((file) => {
      const pdf = isPdfFile(file.fileName);
      const image = isImageFile(file.fileName);
      return {
        kind: ROW_KIND.FILE,
        key: `file-${file.id}`,
        id: file.id,
        displayName: file.documentName || getRealFileName(file.fileName) || file.fileName,
        typeLabel: pdf ? "PDF" : image ? "Photo" : "File",
        badgeStyle: pdf ? PDF_BADGE_STYLE : image ? PHOTO_BADGE_STYLE : DEFAULT_BADGE_STYLE,
        tileStyle: pdf ? FILE_TILE.pdf : image ? FILE_TILE.photo : FILE_TILE.default,
        glyph: <PdfGlyph />,
        // Photo forms render the actual image as the tile thumbnail instead of a
        // glyph; PDFs/other files keep the glyph.
        thumbnailUrl: image && file.fileName ? getUploadUrl(file.fileName) : null,
        uploaderName: getFileUploaderName(file),
        uploaderAvatar: file?.uploadedBy?.avatar,
        createdAt: file.createdAt,
        serviceTicketSupported: true,
        includeInServiceTicket: !!file.includeInServiceTicket,
        raw: file,
      };
    });

    return [...digitalRows, ...fileRows].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [digitalForms, uploadedFiles]);

  // Map uploaded files to the shape AttachmentImageSlider expects.
  const sliderAttachments = useMemo(
    () =>
      uploadedFiles.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        isPdf: isPdfFile(file.fileName),
        displayName: file.documentName || getRealFileName(file.fileName) || file.fileName,
        description: file.description || "",
        tags: file.tags || [],
        createdBy: getFileUploaderName(file),
        createdAt: file.createdAt,
      })),
    [uploadedFiles],
  );

  const handleView = (row) => {
    if (row.kind === ROW_KIND.DIGITAL) {
      setFormViewerShown(true);
      return;
    }
    if (!row.raw?.fileName) return;
    const index = sliderAttachments.findIndex((attachment) => attachment.id === row.id);
    if (index >= 0) setSliderIndex(index);
  };

  const handleToggleServiceTicket = async (row) => {
    if (togglingId) return;
    setTogglingId(row.id);
    try {
      const response = await WOService.updateAttachment({
        id: row.id,
        data: { includeInServiceTicket: !row.includeInServiceTicket },
      });
      if (response.status === 200 || response.status === 201) {
        onRefresh?.();
      } else {
        toast("Something went wrong");
      }
    } catch (error) {
      handleError(error);
    } finally {
      setTogglingId(null);
    }
  };

  const closeMenu = () => {
    setOpenMenuId(null);
    setMenuPosition(null);
  };

  const handleToggleMenu = (event, row) => {
    event.stopPropagation();
    if (openMenuId === row.key) {
      closeMenu();
      return;
    }
    const MENU_WIDTH = 180;
    // Rough height estimate, only used to decide flip direction (not placement).
    const estimatedHeight = 80 + 8;
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < estimatedHeight + 8 && rect.top > spaceBelow;
    // When opening upward, anchor by the menu's *bottom* edge (4px above the
    // button) via `bottom` so the menu hugs the button regardless of its actual
    // height — using an estimated height to compute `top` left a visible gap
    // when the real menu was shorter than the estimate (e.g. digital-form rows
    // have no Download item). Opening downward anchors by `top` as before.
    setMenuPosition({
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      // Right-align the menu to the button.
      left: Math.max(8, rect.right - MENU_WIDTH),
    });
    setOpenMenuId(row.key);
  };

  const handleDownload = (row) => {
    closeMenu();
    const file = row.raw;
    if (!file?.fileName) return;
    const downloadName = getDownloadFileName(file);
    if (isPdfFile(file.fileName)) {
      downloadPdf(getUploadUrl(file.fileName), downloadName);
    } else {
      downloadImage(getUploadUrl(file.fileName), downloadName);
    }
  };

  const handleDelete = async () => {
    const row = fileToDelete;
    setFileToDelete(null);
    if (!row?.id || !order?.id) return;
    try {
      if (row.kind === ROW_KIND.DIGITAL) {
        // Unassign the digital form from the WO rather than deleting the form.
        await WOFormService.update(row.id, { status: 0, wo: null });
        toast("Form unassigned successfully");
      } else {
        await WOService.deleteForm(order.id, row.id, ATTACHMENT_UPLOAD_FLOW);
        toast("Form deleted successfully");
      }
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  return (
    <div className="w-full bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3]">
      {/* Header (click anywhere to expand/collapse) */}
      <div
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full h-[52px] px-6 flex items-center justify-between bg-[#F8F9FE] border-b border-[#E5E8F3] cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
            Forms
          </span>
          <span className="min-w-[24px] px-2 py-[2px] flex items-center justify-center rounded-[16px] bg-[#EEF0F9] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
            {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-4">
          {can("wo:addForm") && onAddForm && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAddForm();
              }}
              className="h-[32px] px-3 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white cursor-pointer"
            >
              Add Form
            </button>
          )}
          <ChevronIcon expanded={expanded} />
        </div>
      </div>

      {/* Grid-rows 0fr->1fr animates the body height without a fixed max-height. */}
      <div
        className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="w-full min-h-0 overflow-hidden">
          {/* Column headers */}
          <div className="w-full grid grid-cols-[minmax(0,2fr)_1fr_1.2fr_1.4fr_0.9fr_auto] items-center gap-3 h-[44px] px-6 bg-[#FBFCFF] border-b border-[#E5E8F3]">
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              File name
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Type
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Uploaded By
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Uploaded Date
            </span>
            <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
              Service Ticket
            </span>
            <span className="w-[100px]" />
          </div>

          {rows.length === 0 ? (
            <div className="w-full px-6 py-6">
              <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">No forms</span>
            </div>
          ) : (
            rows.map((row) => (
              <div
                key={row.key}
                className="w-full grid grid-cols-[minmax(0,2fr)_1fr_1.2fr_1.4fr_0.9fr_auto] items-center gap-3 h-[72px] px-6 border-b border-[#E5E8F3]"
              >
                {/* File name */}
                <div className="min-w-0 flex items-center gap-3">
                  <FileTypeIcon tile={row.tileStyle} glyph={row.glyph} thumbnailUrl={row.thumbnailUrl} />
                  <span
                    className="min-w-0 font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate"
                    title={row.displayName}
                  >
                    {row.displayName}
                  </span>
                </div>

                {/* Type badge */}
                <div className="min-w-0">
                  <span
                    className={`inline-block px-2 py-[2px] rounded-[6px] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize ${row.badgeStyle}`}
                  >
                    {row.typeLabel}
                  </span>
                </div>

                {/* Uploaded by */}
                <div className="min-w-0 flex items-center gap-3">
                  {row.uploaderAvatar && (
                    <img
                      src={getAvatarUrl(row.uploaderAvatar)}
                      alt=""
                      className="w-[24px] h-[24px] rounded-full object-cover"
                    />
                  )}
                  <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477] truncate">
                    {row.uploaderName}
                  </span>
                </div>

                {/* Uploaded date */}
                <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                  {row.createdAt ? formatDate(row.createdAt, "MMMM D, YYYY h:mm A") : "-"}
                </span>

                {/* Service ticket toggle (uploaded files only) */}
                <div>
                  {row.serviceTicketSupported ? (
                    <ServiceTicketToggle
                      checked={row.includeInServiceTicket}
                      disabled={!can("wo:updateAttachment") || togglingId === row.id}
                      onChange={() => handleToggleServiceTicket(row)}
                    />
                  ) : (
                    <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">-</span>
                  )}
                </div>

                {/* View + kebab menu */}
                <div className="w-[100px] flex items-center justify-end gap-3 relative">
                  <button
                    type="button"
                    onClick={() => handleView(row)}
                    className="h-[32px] px-1 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#0065FD] cursor-pointer"
                  >
                    View
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleToggleMenu(e, row)}
                    className="p-[10px] rounded-[8px] text-[#5F6477] cursor-pointer hover:bg-slate-50"
                  >
                    &#8942;
                  </button>
                  {openMenuId === row.key &&
                    menuPosition &&
                    createPortal(
                      <>
                        {/* Click-away overlay */}
                        <div className="fixed inset-0 z-[1000]" onClick={closeMenu} />
                        <div
                          className="fixed z-[1001] w-[180px] flex flex-col gap-[2px] px-1 py-1 bg-white rounded-[12px] border border-[#E5E8F3] shadow-[0px_4px_12px_0px_rgba(10,12.67,18,0.10)]"
                          style={{ top: menuPosition.top, bottom: menuPosition.bottom, left: menuPosition.left }}
                        >
                          {/* Digital forms have no stored file to download. */}
                          {row.kind === ROW_KIND.FILE && (
                            <button
                              type="button"
                              onClick={() => handleDownload(row)}
                              className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                            >
                              <DownloadMenuIcon />
                              <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#1A2332]">
                                Download
                              </span>
                            </button>
                          )}
                          {can("wo:deleteForm") && (
                            <button
                              type="button"
                              onClick={() => {
                                closeMenu();
                                setFileToDelete(row);
                              }}
                              className="w-full min-h-[36px] px-3 py-[6px] flex items-center gap-3 rounded-[8px] text-left hover:bg-[#F8F9FE]"
                            >
                              <DeleteMenuIcon />
                              <span className="flex-1 font-InterMedium text-[14px] leading-[20px] text-[#EF4444]">
                                {row.kind === ROW_KIND.DIGITAL ? "Unassign form" : "Delete file"}
                              </span>
                            </button>
                          )}
                        </div>
                      </>,
                      window.document.body,
                    )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <ConfirmDialog
        title={fileToDelete?.kind === ROW_KIND.DIGITAL ? "UNASSIGN FORM" : "DELETE FORM"}
        description={
          fileToDelete?.kind === ROW_KIND.DIGITAL
            ? "Are you sure you want to unassign this form from the Work Order?"
            : "Are you sure you want to delete this form?"
        }
        shown={!!fileToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setFileToDelete(null)}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderAttachments}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />

      <FormViewerModal
        shown={formViewerShown}
        title="PREVIEW FORM"
        forms={digitalForms}
        onUnassign={() => {
          onRefresh?.();
        }}
        onClose={() => setFormViewerShown(false)}
      />
    </div>
  );
}

export default WoForms;
